import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import UserInviteTermsAcceptance from "./UserInviteTermsAcceptance";

// Always returns a string. Handles plain-string bodies, { message }, and ASP.NET Core
// ProblemDetails ({ title, detail, errors }) — never returns an object (which would crash
// React when rendered as a child).
const extractErrorMessage = (err) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return data?.message || data?.detail || data?.title || err?.message || "An unexpected error occurred.";
};

// Poll the org's provisioning status until its schema is ready. Returns true once ready, or false if
// it doesn't become ready within the deadline. Completion no longer blocks server-side on the schema
// build (that risked a gateway 504 / "Network Error"), so the client waits here instead. Polls are
// best-effort — transient errors are swallowed and retried while the org/session settle.
const waitForSchemaReady = async (organizationId, { timeoutMs = 5 * 60 * 1000, intervalMs = 3000 } = {}) => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    try {
      const res = await api.get(`/userinvite/provisioning-status?organizationId=${organizationId}`);
      if (res.data?.ready) return true;
    } catch {
      // transient (e.g. membership/session still propagating) — keep polling until the deadline
    }
  }
  return false;
};

// Redemption refusals that mean "you are signed in as the wrong account" (customer-tenant mode).
// For these the error panel also offers signing out, so the user can sign in as the invitee.
const EMAIL_MISMATCH_FALLBACK = "Sign in with the email address this invitation was sent to.";
const IDENTITY_CONFLICT_MESSAGE =
  "This sign-in is already linked to a different Axaris account. Sign out and sign in with the account the invitation was sent to.";

const wrongAccountMessage = (err) => {
  if (err?.response?.status !== 403) return null;
  const data = err.response.data;
  if (data?.error === "invite_email_mismatch") {
    return typeof data.message === "string" && data.message.trim() ? data.message : EMAIL_MISMATCH_FALLBACK;
  }
  if (data?.error === "identity_conflict") return IDENTITY_CONFLICT_MESSAGE;
  return null;
};

const InviteCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("loading"); // 'loading' | 'terms' | 'completing' | 'provisioning' | 'declined' | 'error'
  const [inviteDetails, setInviteDetails] = useState(null);
  const [inviteToken, setInviteToken] = useState(null);
  const [showSignOut, setShowSignOut] = useState(false);
  const processingRef = useRef(false);

  // The user arrives here after the backend OIDC sign-in (so a session cookie is set). Validate
  // the invite token, then show T&C; completion is provisioned server-side from the cookie identity.
  useEffect(() => {
    const processInvite = async () => {
      if (processingRef.current) return;
      processingRef.current = true;

      try {
        // Remove any leftover hash fragment from the redirect.
        if (window.location.hash) {
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
        }

        const rawToken = new URLSearchParams(window.location.search).get("token")
          || sessionStorage.getItem("inviteToken");
        const token = typeof rawToken === "string" && /^[a-zA-Z0-9_-]{1,128}$/.test(rawToken.trim())
          ? rawToken.trim()
          : null;

        if (!token) {
          setError("Invite token missing or invalid. Please use the invite link from your email.");
          setPhase("error");
          return;
        }

        // Validate the invite token (anonymous) before showing T&C.
        const validateResponse = await axios.get(
          `${API_BASE_URL}/userinvite/validate?token=${encodeURIComponent(token)}`
        );
        setInviteToken(token);
        setInviteDetails(validateResponse.data);
        setPhase("terms");
      } catch (err) {
        setError(extractErrorMessage(err));
        setPhase("error");
      }
    };

    processInvite();
  }, []);

  const completeInvite = useCallback(async () => {
    setPhase("completing");

    try {
      // Provision the account. The backend derives the Azure OID from the session cookie; we only
      // send the invite token. (api adds withCredentials + the X-Axaris-Client CSRF header.)
      const response = await api.post(`/userinvite/complete`, { InviteToken: inviteToken });
      const result = response.data;

      sessionStorage.removeItem("inviteToken");

      // Populate auth context from the provisioning response.
      login(result);

      // Set selected organization in localStorage.
      localStorage.setItem(
        "selectedOrganization",
        JSON.stringify({
          tenantId: result.tenantId || 0,
          organizationId: result.organizationId,
          organizationName: result.organizationName || "",
        })
      );

      // For a fresh OWNER signup the org schema may still be building. Wait for it to be ready before
      // landing on the dashboard, which would otherwise render against an unprovisioned org.
      if (result.schemaReady === false && result.organizationId) {
        setPhase("provisioning");
        const ready = await waitForSchemaReady(result.organizationId);
        if (!ready) {
          setError("Your organization is taking longer than expected to set up. Please refresh in a few minutes, or contact support if the problem persists.");
          setPhase("error");
          return;
        }
      }

      navigate('/dashboard');
    } catch (err) {
      // Signed in as the wrong account: say so and offer signing out (under Oidc, /logout also ends
      // the identity provider session, so the next sign-in asks for credentials).
      const wrongAccount = wrongAccountMessage(err);
      if (wrongAccount) {
        setError(wrongAccount);
        setShowSignOut(true);
        setPhase("error");
        return;
      }
      // Don't surface raw backend messages (e.g. authorization/internal errors) to the user during
      // provisioning — show a friendly generic message and point them to support.
      setError("An unexpected error occurred. Please contact support.");
      setPhase("error");
    }
  }, [inviteToken, login, navigate]);

  const handleDecline = useCallback(() => {
    sessionStorage.removeItem("inviteToken");
    setPhase("declined");
  }, []);

  // Show Terms & Conditions screen
  if (phase === "terms" || phase === "completing") {
    return (
      <UserInviteTermsAcceptance
        onAccept={completeInvite}
        onDecline={handleDecline}
        inviteDetails={inviteDetails}
        loading={phase === "completing"}
      />
    );
  }

  // Loading, error, and declined states
  return (
    <div className="flex items-center justify-center h-screen bg-theme-primary">
      <div className="bg-theme-card-solid border border-border rounded-2xl shadow-xl p-10 w-96 text-center flex flex-col items-center gap-4">
        <div className="logo-wordmark logo-wordmark-lg items-center">
          <span className="logo-wordmark-text logo-wordmark-text-gradient">AXARIS™</span>
          <span className="logo-wordmark-sub text-content-muted">By Cloud Guidant</span>
        </div>
        {(phase === "loading" || phase === "provisioning") && (
          <>
            <p className="text-content-secondary">
              {phase === "provisioning"
                ? "Setting up your organization. This can take a minute…"
                : "Setting up your account..."}
            </p>
            <div className="animate-spin rounded-full border-2 border-transparent border-t-accent-cyan border-r-accent-purple h-8 w-8"></div>
          </>
        )}
        {phase === "error" && (
          <>
            <p className="text-red-400">{error}</p>
            {showSignOut && (
              <a href="/logout" className="text-accent-cyan hover:underline">Sign out and use a different account</a>
            )}
            <a href="/login" className="text-accent-cyan hover:underline">Return to Login</a>
          </>
        )}
        {phase === "declined" && (
          <>
            <p className="text-content-secondary">
              You have declined the Terms &amp; Conditions. Your account has not been created.
            </p>
            <a href="/login" className="text-accent-cyan hover:underline">Return to Login</a>
          </>
        )}
      </div>
    </div>
  );
};

export default InviteCallback;
